
import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';

export default function Pricing() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  const plans = [
    {
      id: 'free',
      name: 'Free',
      description: '개인 및 소규모 교육기관을 위한 무료 플랜',
      monthlyPrice: 0,
      yearlyPrice: 0,
      yearlyDiscount: 0,
      features: [
        '템플릿 2개',
        '월 100건 증명서 발급',
        '월 1회 일괄 다운로드',
        '엑셀 업로드 컬럼 10개',
        '유료 수료증 발급 기능',
        '이메일 지원'
      ],
      recommended: false,
      color: 'gray'
    },
    {
      id: 'basic',
      name: 'Basic',
      description: '소규모 교육기관을 위한 기본 플랜',
      monthlyPrice: 29000,
      yearlyPrice: 295800,
      yearlyDiscount: 15,
      features: [
        '템플릿 5개',
        '월 1,000건 증명서 발급',
        '월 10회 일괄 다운로드',
        '엑셀 업로드 컬럼 30개',
        '유료 수료증 발급 기능',
        '이메일 지원'
      ],
      recommended: false,
      color: 'blue'
    },
    {
      id: 'pro',
      name: 'Pro',
      description: '중간 규모 교육기관을 위한 인기 플랜',
      monthlyPrice: 99000,
      yearlyPrice: 950400,
      yearlyDiscount: 20,
      features: [
        '템플릿 10개',
        '월 10,000건 증명서 발급',
        '월 100회 일괄 다운로드',
        '엑셀 업로드 컬럼 40개',
        '유료 수료증 발급 기능',
        'API 지원',
        '이메일 지원'
      ],
      recommended: true,
      color: 'purple'
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: '대규모 교육기관을 위한 완전한 솔루션',
      monthlyPrice: null,
      yearlyPrice: null,
      yearlyDiscount: 0,
      features: [
        '무제한 템플릿',
        '무제한 증명서 발급',
        '무제한 일괄 다운로드',
        '무제한 엑셀 업로드 컬럼',
        '유료 수료증 발급 기능',
        'API 지원',
        '이메일 지원 (SLA 옵션)'
      ],
      recommended: false,
      color: 'indigo'
    }
  ];

  const getPrice = (plan: typeof plans[0]) => {
    if (plan.monthlyPrice === null) return null;
    const price = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
    return price;
  };

  const getPriceDisplay = (plan: typeof plans[0]) => {
    const price = getPrice(plan);
    if (price === null) return '별도 문의';
    if (price === 0) return '무료';
    return `₩${price.toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            합리적인 <span className="text-blue-600">요금제</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            기관의 규모와 필요에 맞는 플랜을 선택하세요. 
            언제든지 플랜을 변경할 수 있습니다.
          </p>
          
          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-white rounded-lg p-1 shadow-sm">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                billingCycle === 'monthly'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              월간 결제
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                billingCycle === 'yearly'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              연간 결제
              <span className="ml-2 text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full whitespace-nowrap">
                최대 20% 할인
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* 2026년 프로모션 안내 */}
      <section className="py-12 bg-gradient-to-r from-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-purple-200">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <i className="ri-gift-line text-5xl text-purple-600"></i>
              </div>
              <div className="ml-6 flex-1">
                <h2 className="text-2xl font-bold text-purple-900 mb-3">
                  🎁 2026년 신규 기관 대상 프로모션
                </h2>
                <p className="text-lg text-purple-800 mb-4">
                  <strong>2026년 1월 1일 ~ 2026년 3월 1일</strong> 사이에 가입한 기관은 <strong className="text-purple-900">Basic 플랜을 1년간 무료</strong>로 제공합니다!
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start">
                    <i className="ri-check-line text-purple-600 mr-2 mt-0.5 flex-shrink-0"></i>
                    <span className="text-sm text-purple-700">이벤트 기간 중 가입 시 자동 적용되며, 1년 후에는 선택한 플랜에 따라 정상 요금 적용</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-purple-600 mr-2 mt-0.5 flex-shrink-0"></i>
                    <span className="text-sm text-purple-700">모든 신규 기관에 적용</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-purple-600 mr-2 mt-0.5 flex-shrink-0"></i>
                    <span className="text-sm text-purple-700">결제 정보 등록 없이도 Basic 기능 이용 가능</span>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-check-line text-purple-600 mr-2 mt-0.5 flex-shrink-0"></i>
                    <span className="text-sm text-purple-700">1년 무료 기간 동안 Pro 및 Enterprise로 업그레이드 가능</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {plans.map((plan, index) => (
              <Card 
                key={index} 
                className={`relative ${
                  plan.recommended 
                    ? 'border-2 border-blue-600 shadow-lg scale-105' 
                    : 'border border-gray-200'
                }`}
              >
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-medium whitespace-nowrap">
                      가장 인기
                    </span>
                  </div>
                )}
                
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-gray-600 mb-6 min-h-[48px]">
                    {plan.description}
                  </p>
                  
                  <div className="mb-6 min-h-[80px] flex flex-col items-center justify-center">
                    {plan.monthlyPrice === null ? (
                      <span className="text-3xl font-bold text-gray-900">별도 문의</span>
                    ) : plan.monthlyPrice === 0 ? (
                      <span className="text-4xl font-bold text-gray-900">무료</span>
                    ) : (
                      <>
                        <span className="text-4xl font-bold text-gray-900">
                          {getPriceDisplay(plan)}
                        </span>
                        <span className="text-gray-600 mt-1">
                          / {billingCycle === 'monthly' ? '월' : '년'}
                        </span>
                        {billingCycle === 'yearly' && plan.yearlyDiscount > 0 && (
                          <div className="text-sm text-green-600 mt-1">
                            연간 결제 시 {plan.yearlyDiscount}% 할인
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  
                  <Button 
                    className={`w-full mb-6 whitespace-nowrap ${
                      plan.recommended 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : plan.id === 'enterprise'
                        ? 'bg-purple-600 hover:bg-purple-700'
                        : 'bg-gray-600 hover:bg-gray-700'
                    }`}
                  >
                    {plan.id === 'enterprise' ? '상담 문의' : plan.id === 'free' ? '무료 시작' : '무료 체험 시작'}
                  </Button>
                  
                  <ul className="text-left space-y-3">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <i className="ri-check-line text-green-600 mr-3 mt-0.5 flex-shrink-0"></i>
                        <span className="text-gray-600 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 상세 기능 비교표 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              상세 기능 비교
            </h2>
            <p className="text-xl text-gray-600">
              플랜별 제공되는 기능을 자세히 비교해보세요.
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto shadow-sm">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">기능</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Free</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Basic</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900 bg-blue-50">Pro</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Enterprise</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">템플릿 수</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center">2개</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center">5개</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center bg-blue-50">10개</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center">무제한</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">월 발급 수량</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center">100건</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center">1,000건</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center bg-blue-50">10,000건</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center">무제한</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">일괄 다운로드</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center">월 1회</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center">월 10회</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center bg-blue-50">월 100회</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center">무제한</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">엑셀 업로드 컬럼 제한</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center">10개</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center">30개</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center bg-blue-50">40개</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center">무제한</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">API 지원</td>
                  <td className="px-6 py-4 text-center">
                    <i className="ri-close-line text-red-600 text-xl"></i>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <i className="ri-close-line text-red-600 text-xl"></i>
                  </td>
                  <td className="px-6 py-4 text-center bg-blue-50">
                    <i className="ri-check-line text-green-600 text-xl"></i>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <i className="ri-check-line text-green-600 text-xl"></i>
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">유료 수료증 발급 기능</td>
                  <td className="px-6 py-4 text-center">
                    <i className="ri-check-line text-green-600 text-xl"></i>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <i className="ri-check-line text-green-600 text-xl"></i>
                  </td>
                  <td className="px-6 py-4 text-center bg-blue-50">
                    <i className="ri-check-line text-green-600 text-xl"></i>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <i className="ri-check-line text-green-600 text-xl"></i>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">지원(Support)</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center">이메일</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center">이메일</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center bg-blue-50">이메일</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-center">이메일 (SLA 옵션)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              자주 묻는 질문
            </h2>
            <p className="text-xl text-gray-600">
              요금제에 대해 궁금한 점들을 확인해보세요.
            </p>
          </div>

          <div className="space-y-6">
            {[
              {
                question: '무료 체험 기간은 얼마나 되나요?',
                answer: '14일간 무료로 모든 기능을 체험할 수 있습니다. 신용카드 등록 없이도 바로 시작할 수 있어요.'
              },
              {
                question: '플랜을 중간에 변경할 수 있나요?',
                answer: '네, 언제든지 플랜을 업그레이드하거나 다운그레이드할 수 있습니다. 변경된 요금은 다음 결제일부터 적용됩니다.'
              },
              {
                question: '증명서 발급 한도를 초과하면 어떻게 되나요?',
                answer: '한도 초과 시 건당 추가 요금이 발생하거나, 상위 플랜으로 자동 업그레이드를 선택할 수 있습니다.'
              },
              {
                question: '환불 정책은 어떻게 되나요?',
                answer: '서비스에 만족하지 않으시면 30일 이내 100% 환불해드립니다. 별도 위약금은 없습니다.'
              },
              {
                question: '엔터프라이즈 플랜의 맞춤 기능은 무엇인가요?',
                answer: '전용 서버, 맞춤형 개발, 온프레미스 설치, 전담 지원팀 등 기업의 특별한 요구사항에 맞춘 서비스를 제공합니다.'
              }
            ].map((faq, index) => (
              <Card key={index} className="hover:shadow-md transition-shadow">
                <details className="group">
                  <summary className="flex justify-between items-center cursor-pointer">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {faq.question}
                    </h3>
                    <i className="ri-arrow-down-s-line text-xl text-gray-400 group-open:rotate-180 transition-transform flex-shrink-0 ml-2"></i>
                  </summary>
                  <p className="mt-4 text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </details>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            지금 바로 시작해보세요
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            14일 무료 체험으로 CEIS의 모든 기능을 경험해보세요.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-50 whitespace-nowrap">
              <i className="ri-rocket-line"></i>
              무료 체험 시작하기
            </Button>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10 whitespace-nowrap">
              <i className="ri-phone-line"></i>
              상담 문의하기
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
